import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import LabelChip from './LabelChip';

// Compact chip dimensions for label display in narrow card and table-cell layouts
const COMPACT_LABEL_CHIP_HEIGHT = 18;
const COMPACT_LABEL_CHIP_FONT_SIZE = '10px';
const COMPACT_LABEL_CHIP_MAX_WIDTH = 'min(120px, 100%)';

const compactLabelChipSx = {
  height: COMPACT_LABEL_CHIP_HEIGHT,
  fontSize: COMPACT_LABEL_CHIP_FONT_SIZE,
  maxWidth: COMPACT_LABEL_CHIP_MAX_WIDTH,
};

interface LabelListProps {
  compact?: boolean;
  disableTooltips?: boolean;
  labels: Array<{ id: string; name: string; color: string }>;
  max?: number;
  sx?: SxProps<Theme>;
}

export default function LabelList({
  compact = false,
  disableTooltips = false,
  labels,
  max = 3,
  sx,
}: LabelListProps) {
  const theme = useTheme();

  if (labels.length === 0) {
    return null;
  }

  const visibleLabels = labels.slice(0, max);
  const remainingLabels = labels.slice(max);
  const remainingCount = remainingLabels.length;
  const remainingCountLabel = t('labels.more_count', { count: remainingCount });
  const remainingCountIndicator = compact ? (
    <Typography
      component="span"
      sx={{
        color: theme.brandV2.colors.treadGray2,
        fontSize: COMPACT_LABEL_CHIP_FONT_SIZE,
      }}
    >
      {remainingCountLabel}
    </Typography>
  ) : (
    <Chip label={remainingCountLabel} size="small" />
  );

  return (
    <Box
      data-test-id="label-list"
      sx={[
        {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {visibleLabels.map((label) => (
        <LabelChip
          key={label.id}
          name={label.name}
          color={label.color}
          disableTooltip={disableTooltips}
          sx={compact ? compactLabelChipSx : undefined}
        />
      ))}
      {remainingCount > 0 &&
        (disableTooltips ? (
          remainingCountIndicator
        ) : (
          <Tooltip title={remainingLabels.map((label) => label.name).join(', ')}>
            {remainingCountIndicator}
          </Tooltip>
        ))}
    </Box>
  );
}
